import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table } from "antd";
import MainLayout from "../../Layout/mainLayout";
import { columnsUserViewVrHistory } from "../../Global Functions/columnsAntd";
import { viewUserViewVrHistoryList, deleteUserViewVrHistory, viewProjectList } from "../../Redux/Actions/userViewVrHistoryActions";
import ListTopSearch from "../../Global Functions/listTopSearch";
import DateRangePopup from "../../Utils/dateFilterPopup";
import ProjectFilterDropdown from "./projectFilterDropdown";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";

const ListUserViewVrHistory = () => {
    const dispatch = useDispatch();

    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState(searchQuery);
    const [isDatePopupVisible, setIsDatePopupVisible] = useState(false);
    const [dateRange, setDateRange] = useState({ start: null, end: null });
    const [deletingId, setDeletingId] = useState(null);
    const [selectedProject, setSelectedProject] = useState("");

    const { admin } = useSelector((state) => state.admin || {});
    const accessCode = admin?.data?.access_code || [];
    const canAdd = Array.isArray(accessCode) && accessCode.includes('add');
    const canEdit = Array.isArray(accessCode) && accessCode.includes('edit');

    const { userViewVrHistoryList, loading, projectList } = useSelector((state) => state.userViewVrHistory || {});

    const derivedProjects = useMemo(() => {
        const rows = Array.isArray(userViewVrHistoryList?.data) ? userViewVrHistoryList.data : [];
        const map = new Map();
        rows.forEach((row) => {
            if (row.project?._id && !map.has(row.project._id)) {
                map.set(row.project._id, row.project);
            }
        });
        return Array.from(map.values());
    }, [userViewVrHistoryList]);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
        showSizeChanger: true,
        pageSizeOptions: ["5", "10", "20", "50", "100"],
    });

    useEffect(() => {
        dispatch(viewProjectList({ limit: 1000 }));
    }, [dispatch]);

    const fetchDetails = useCallback(async (page, pageSize, showLoader = true) => {
        const payload = {
            page,
            limit: pageSize,
            is_deleted: false,
            filter: debouncedSearch,
            ...(selectedProject && { project: selectedProject }),
            ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
            ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
        };
        try {
            await dispatch(viewUserViewVrHistoryList(payload));
            if (!showLoader) toast.success("List refreshed");
        } catch {
            if (showLoader) toast.error("Failed to fetch list");
        }
    }, [dispatch, debouncedSearch, dateRange, selectedProject]);

    useEffect(() => {
        fetchDetails(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, fetchDetails]);

    useEffect(() => {
        if (userViewVrHistoryList?.total) {
            setPagination((prev) => ({ ...prev, total: userViewVrHistoryList.total }));
        }
    }, [userViewVrHistoryList]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchQuery]);

    const handleTableChange = (pagination) => setPagination(pagination);

    const handleProjectChange = (e) => {
        setSelectedProject(e.target.value);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleDateRangeApply = (start, end) => {
        if (start === null && end === null) {
            setDateRange({ start: null, end: null });
        } else {
            setDateRange({ start, end });
        }
        setIsDatePopupVisible(false);
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const handleDelete = async (id) => {
        setDeletingId(id);
        try {
            await dispatch(deleteUserViewVrHistory(id));
            toast.success("Record deleted successfully");
            fetchDetails(pagination.current, pagination.pageSize, false);
        } catch {
            toast.error("Failed to delete record");
        } finally {
            setDeletingId(null);
        }
    };

    const handleDownloadVrHistoryExcel = async () => {
        try {
            const data = {
                page: pagination.current,
                limit: pagination.pageSize,
                filter: debouncedSearch,
                is_deleted: false,
                ...(selectedProject && { project: selectedProject }),
                ...(dateRange.start && { start_date: dateRange.start.format("YYYY-MM-DD") }),
                ...(dateRange.end && { end_date: dateRange.end.format("YYYY-MM-DD") }),
            };

            const result = await dispatch(viewUserViewVrHistoryList(data));
            const rawData = result?.data || userViewVrHistoryList?.data || [];
            if (!Array.isArray(rawData) || rawData.length === 0) {
                alert("No VR history data available to download");
                return;
            }

            const preparedData = rawData.map((row) => ({
                _id: row._id,
                user_name: row.user?.name || "",
                user_id: row.user?.user_id || "",
                email: row.user?.email || "",
                contact_number: row.user?.contact_number || "",
                business_type: row.business_type || "",
                project_name: row.project?.project_name || "",
                created_at: row.created_at,
            }));

            const worksheet = XLSX.utils.json_to_sheet(preparedData);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, "VR History");
            XLSX.writeFile(workbook, "user_vr_history_data.xlsx");
        } catch (error) {
            alert("Failed to download VR history data");
        }
    };

    return (
        <MainLayout>
            <div className="bookings_table">
                <div className="table_component_all">
                    <div style={{ gridColumn: "span 5", height: "auto" }}>
                        <ListTopSearch
                            h3heading="User VR View History"
                            setState={setSearchQuery}
                            onCalendarClick={() => setIsDatePopupVisible(true)}
                            onDownloadBooking={canAdd && canEdit ? handleDownloadVrHistoryExcel : null}
                        />
                    </div>
                    <div
                        style={{
                            position: "static",
                            marginBottom: "3%",
                            marginTop: "-5.7%",
                            marginLeft: "83.5%"
                        }}
                    >
                        <ProjectFilterDropdown
                            value={selectedProject}
                            onChange={handleProjectChange}
                            projects={projectList?.data || []}
                        />
                    </div>

                    <div className="custom_table">
                        <div style={{ padding: "0px 60px" }}>
                            <Table
                                dataSource={(Array.isArray(userViewVrHistoryList?.data) && userViewVrHistoryList.data) || []}
                                columns={columnsUserViewVrHistory({ onDelete: handleDelete, deletingId, canEdit })}
                                pagination={pagination}
                                loading={loading}
                                onChange={handleTableChange}
                                scroll={{ x: 1700, y: 500 }}
                                rowKey="_id"
                                rowClassName={() => "text-center"}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <DateRangePopup
                visible={isDatePopupVisible}
                initialStart={dateRange.start}
                initialEnd={dateRange.end}
                onCancel={() => setIsDatePopupVisible(false)}
                onOk={handleDateRangeApply}
            />
        </MainLayout>
    );
};

export default ListUserViewVrHistory;